import type { GridApi } from 'ag-grid-community';
import type {
  AuditLogApiFilter,
  AuditLogFilterCondition,
  AuditLogFilterValue,
  AuditLogSorting,
  AuditLogsHistoryRequest,
} from '@circuitry-ai/doc-data';
import { agGridToApiCondition } from '../utils/constants';

const AUDIT_LOG_SORT_FIELD_MAP: Record<string, string> = {
  audit_type: 'audit_type',
  module: 'module',
  sub_module: 'sub_module',
  resource_id: 'resource_id',
  description: 'description',
  executed_by: 'executed_by',
  executed_at: 'executed_at',
  status: 'status',
};

type AgGridFilterModelEntry = Record<string, unknown>;

/** Maps AG Grid condition keys to audit logs history API conditions. */
export function normalizeAuditLogFilterCondition(condition: string): AuditLogFilterCondition {
  if (condition === 'EQ') return 'equals';
  const mapped = agGridToApiCondition[condition];
  if (mapped) return mapped as AuditLogFilterCondition;
  return condition as AuditLogFilterCondition;
}

/** Builds a typed audit log API filter with a single field key. */
export function createAuditLogApiFilter(
  field: string,
  value: AuditLogFilterValue | string[],
  condition: AuditLogFilterCondition
): AuditLogApiFilter {
  return { [field]: value, condition };
}

function toAgGridFilterType(value: unknown, fallback: string): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
}

function toFilterValue(value: unknown): AuditLogFilterValue | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number') return value;
  return null;
}

function toDatePart(value: unknown): string | null {
  if (typeof value === 'string') return value.split(' ')[0];
  if (typeof value === 'number') return String(value);
  return null;
}

function toSetFilterValues(values: unknown[]): string[] {
  const result: string[] = [];
  for (const item of values) {
    if (typeof item === 'string') result.push(item);
    else if (typeof item === 'number') result.push(String(item));
  }
  return result;
}

function appendSetFilter(
  filters: AuditLogApiFilter[],
  field: string,
  filter: AgGridFilterModelEntry
): void {
  const rawValues = Array.isArray(filter.values) ? filter.values : [];
  const values = toSetFilterValues(rawValues);
  if (values.length === 0) return;
  const condition: AuditLogFilterCondition = filter.exclude ? 'not_in' : 'in';
  filters.push(createAuditLogApiFilter(field, values, condition));
}

function appendNumberOrCondition(
  filters: AuditLogApiFilter[],
  field: string,
  conditionEntry: AgGridFilterModelEntry
): void {
  const value = toFilterValue(conditionEntry.filter);
  if (value === null) return;
  const typeKey = toAgGridFilterType(conditionEntry.type, 'equals');
  filters.push(
    createAuditLogApiFilter(field, value, normalizeAuditLogFilterCondition(typeKey))
  );
}

function appendNumberOrFilters(
  filters: AuditLogApiFilter[],
  field: string,
  filter: AgGridFilterModelEntry
): void {
  if (filter.condition1) {
    appendNumberOrCondition(filters, field, filter.condition1 as AgGridFilterModelEntry);
  }
  if (filter.condition2) {
    appendNumberOrCondition(filters, field, filter.condition2 as AgGridFilterModelEntry);
  }
}

function appendDateFilters(
  filters: AuditLogApiFilter[],
  field: string,
  filter: AgGridFilterModelEntry
): void {
  const type = toAgGridFilterType(filter.type, '');
  const from = toDatePart(filter.dateFrom ?? filter.filter);
  const to = toDatePart(filter.dateTo);

  if (type === 'inRange' && from && to) {
    filters.push(createAuditLogApiFilter(field, from, 'gte'));
    filters.push(createAuditLogApiFilter(field, to, 'lte'));
    return;
  }
  if (type === 'lessThan' && from) {
    filters.push(createAuditLogApiFilter(field, from, 'lt'));
    return;
  }
  if (type === 'greaterThan' && from) {
    filters.push(createAuditLogApiFilter(field, from, 'gt'));
    return;
  }
  if (from) {
    const condition = normalizeAuditLogFilterCondition(type || 'equals');
    filters.push(createAuditLogApiFilter(field, from, condition));
  }
}

function appendTextFilter(
  filters: AuditLogApiFilter[],
  field: string,
  filter: AgGridFilterModelEntry
): void {
  const cond = normalizeAuditLogFilterCondition(toAgGridFilterType(filter.type, 'contains'));
  if (filter.type === 'blank' || filter.type === 'notBlank') {
    filters.push(createAuditLogApiFilter(field, '', cond));
    return;
  }
  const value = toFilterValue(filter.filter);
  if (value !== null) {
    filters.push(createAuditLogApiFilter(field, value, cond));
  }
}

function appendFilterForField(
  filters: AuditLogApiFilter[],
  field: string,
  filter: AgGridFilterModelEntry
): void {
  if (filter.filterType === 'set') {
    appendSetFilter(filters, field, filter);
    return;
  }
  if (filter.filterType === 'number' && filter.operator === 'OR') {
    appendNumberOrFilters(filters, field, filter);
    return;
  }
  if (filter.filterType === 'date') {
    appendDateFilters(filters, field, filter);
    return;
  }
  appendTextFilter(filters, field, filter);
}

/**
 * Maps AG Grid filter model to audit logs history API filters.
 * Mirrors conversation history `buildApiFilters` behavior.
 */
export function buildAuditLogApiFilters(
  gridApi: GridApi | null | undefined,
  staticFilters: AuditLogApiFilter[] = []
): AuditLogApiFilter[] {
  const filters: AuditLogApiFilter[] = [...staticFilters];
  const model = gridApi?.getFilterModel?.() ?? {};

  for (const [field, filter] of Object.entries(model)) {
    if (!filter || typeof filter !== 'object') continue;
    appendFilterForField(filters, field, filter as AgGridFilterModelEntry);
  }

  return filters;
}

/** Maps AG Grid column id and sort direction to audit logs history API sorting. */
export function buildAuditLogApiSorting(
  colId: string | null | undefined,
  sort: 'asc' | 'desc' | null | undefined
): AuditLogSorting | undefined {
  if (!colId || !sort) return undefined;
  const field = AUDIT_LOG_SORT_FIELD_MAP[colId] ?? colId;
  return { field, order: sort };
}

/** Reads the active AG Grid sort column and returns API sorting payload. */
export function buildAuditLogApiSortingFromGrid(
  gridApi: GridApi | null | undefined
): AuditLogSorting | undefined {
  const state = gridApi?.getColumnState?.().find((column) => !!column.sort);
  if (!state?.colId || !state.sort) return undefined;
  const order = state.sort === 'asc' || state.sort === 'desc' ? state.sort : undefined;
  return buildAuditLogApiSorting(state.colId, order);
}

/** Builds full POST body for audit logs history (filters, pagination, optional sorting). */
export function buildAuditLogsHistoryRequest({
  gridApi,
  staticFilters = [],
  page,
  perPage,
  sorting,
}: {
  gridApi: GridApi | null | undefined;
  staticFilters?: AuditLogApiFilter[];
  page: number;
  perPage: number;
  sorting?: AuditLogSorting;
}): AuditLogsHistoryRequest {
  const filters = buildAuditLogApiFilters(gridApi, staticFilters);
  const resolvedSorting = sorting ?? buildAuditLogApiSortingFromGrid(gridApi);

  const request: AuditLogsHistoryRequest = {
    filters,
    pagination: { page, per_page: perPage },
  };

  if (resolvedSorting) {
    request.sorting = resolvedSorting;
  }

  return request;
}
